'use client'

import { useAppDispatch, useAppSelector } from '@/lib/store';
import { useEffect, useState } from 'react';
import { showToast } from '@/lib/utils/toast';

// Custom type for the event detail
interface ServiceResetEventDetail {
  reason: string;
  previousServices: number;
  forceRepeat: boolean;
}

interface TimeSlotResetEventDetail {
  reason: string;
  previousDuration: number;
  newDuration: number;
  forceRepeat: boolean;
}

/**
 * Component that listens for critical field change events and displays
 * a toast notification allowing users to confirm or reject the changes
 */
export function CriticalFieldChangeNotification() {
  const dispatch = useAppDispatch()
  const [resetFlags, setResetFlags] = useState({
    servicesReset: false,
    timeSlotsReset: false
  })

  // Get current state to determine which steps need attention
  const selectedServiceIds = useAppSelector((state) => state.booking.persisted.selectedServiceIds)
  // selectedOption replaces the old selectedTimeSlots array in the new model
  const selectedOption = useAppSelector((state) => state.booking.persisted.selectedOption)

  // Reset flags when services or option are selected
  useEffect(() => {
    if (selectedServiceIds.length > 0 && resetFlags.servicesReset) {
      setResetFlags(prev => ({ ...prev, servicesReset: false }))
    }
  }, [selectedServiceIds, resetFlags.servicesReset])

  useEffect(() => {
    if (selectedOption !== null && resetFlags.timeSlotsReset) {
      setResetFlags(prev => ({ ...prev, timeSlotsReset: false }))
    }
  }, [selectedOption, resetFlags.timeSlotsReset])

  useEffect(() => {
    const handleServicesReset = (event: CustomEvent<ServiceResetEventDetail>) => {
      // Only show notification if user must repeat a step
      if (!event.detail.forceRepeat) return;

      // Mark services as reset
      setResetFlags(prev => ({ ...prev, servicesReset: true, timeSlotsReset: true }))

      showToast({
        title: event.detail.reason === 'booking/setShopLocation'
          ? 'Not all your selected services are available at the location you\'ve selected. Your selected services were reset.'
          : 'Services have been reset',
        description: event.detail.reason === 'booking/setShopLocation'
          ? 'Your selected services were reset because not all are available at the new location.'
          : 'Your selected services were reset due to vehicle or location changes.',
        color: 'warning',
        timeout: 5000
      });
    };

    const handleTimeSlotsReset = (event: CustomEvent<TimeSlotResetEventDetail>) => {
      // Only show notification if user must repeat a step
      if (!event.detail.forceRepeat) return;

      // Mark time slots as reset (but not services)
      setResetFlags(prev => ({ ...prev, timeSlotsReset: true }))

      showToast({
        title: 'Appointment time has been reset',
        description: 'Your selected appointment time was reset due to service duration changes.',
        color: 'warning',
        timeout: 5000
      });
    };

    // Add event listeners
    window.addEventListener('appointmentServicesReset', handleServicesReset as EventListener);
    window.addEventListener('appointmentTimeSlotsReset', handleTimeSlotsReset as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('appointmentServicesReset', handleServicesReset as EventListener);
      window.removeEventListener('appointmentTimeSlotsReset', handleTimeSlotsReset as EventListener);
    };
  }, [dispatch, resetFlags, selectedServiceIds, selectedOption]);

  // This component doesn't render anything visually
  return null;
}
